import { useCallback, useEffect, useRef, useState } from 'react';
import {
  fetchAgents,
  createAgent as apiCreateAgent,
  updateAgent as apiUpdateAgent,
  deleteAgent as apiDeleteAgent,
  updateAgentAvatar as apiUpdateAgentAvatar,
} from '../api/agents.api';
import { useAppStore } from '../store/appStore';
import type { Agent } from '../types';

export const useAgents = (workspaceId: string | undefined) => {
  const { agentsByWorkspace, setAgentsForWorkspace } = useAppStore();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const loadingRef = useRef<Set<string>>(new Set());
  const lastWorkspaceIdRef = useRef<string | undefined>(workspaceId);
  const existingForWorkspace = workspaceId ? agentsByWorkspace[workspaceId] : undefined;

  const load = useCallback(async () => {
    if (!workspaceId || workspaceId === 'undefined' || workspaceId === 'null') return;
    // Prevent multiple simultaneous loads for the same workspace
    if (loadingRef.current.has(workspaceId)) return;

    loadingRef.current.add(workspaceId);
    setLoading(true);
    setError(null);
    try {
      const data = await fetchAgents(workspaceId);
      setAgentsForWorkspace(workspaceId, data);
    } catch (err: any) {
      // Don't show error if it's just an invalid workspaceId
      if (err?.message === 'Invalid workspaceId') {
        return;
      }
      const message =
        err?.response?.data?.message ||
        'We could not load agents for this workspace. Please try again.';
      setError(message);
    } finally {
      setLoading(false);
      loadingRef.current.delete(workspaceId);
    }
  }, [setAgentsForWorkspace, workspaceId]);

  useEffect(() => {
    if (!workspaceId || workspaceId === 'undefined' || workspaceId === 'null') return;

    // Reset tracking when workspaceId changes
    if (lastWorkspaceIdRef.current !== workspaceId) {
      lastWorkspaceIdRef.current = workspaceId;
      loadingRef.current.clear();
    }

    const existing = existingForWorkspace;
    // Only load if we haven't loaded for this workspace yet (existing is undefined, not empty array)
    // Empty array [] means we've already loaded and there are no agents
    if (existing === undefined && !loadingRef.current.has(workspaceId)) {
      void load();
    }
  }, [workspaceId, load, existingForWorkspace]);

  const reload = useCallback(() => {
    if (!workspaceId || workspaceId === 'undefined' || workspaceId === 'null') return;
    // Clear tracking to force reload
    loadingRef.current.delete(workspaceId);
    void load();
  }, [workspaceId, load]);

  const createAgent = useCallback(
    async (
      payload: Omit<Agent, 'id' | 'createdAt' | 'status'> & {
        status?: Agent['status'];
        roleTitle?: string;
      },
    ) => {
      const created = await apiCreateAgent({
        workspaceId: payload.workspaceId,
        name: payload.name,
        displayName: payload.name,
        roleTitle: payload.roleTitle,
        description: payload.description,
        systemPrompt: payload.systemPrompt,
        model: payload.model,
        temperature: payload.temperature ?? 0.7,
        status: payload.status ?? 'DRAFT',
        clientApiBaseUrl: payload.clientApiBaseUrl,
        clientApiToken: payload.clientApiToken,
      });
      if (workspaceId) {
        const current = agentsByWorkspace[workspaceId] || [];
        setAgentsForWorkspace(workspaceId, [...current, created]);
      }
      return created;
    },
    [agentsByWorkspace, setAgentsForWorkspace, workspaceId],
  );

  const saveAgent = useCallback(
    async (id: string, updates: Partial<Agent>) => {
      const updated = await apiUpdateAgent(id, {
        name: updates.name,
        description: updates.description,
        systemPrompt: updates.systemPrompt,
        model: updates.model,
        status: updates.status,
        clientApiBaseUrl: updates.clientApiBaseUrl,
        clientApiToken: updates.clientApiToken,
        identityResolverEndpoint: updates.identityResolverEndpoint,
        serviceUrls: updates.serviceUrls,
        adminAlertPhoneNumber: updates.adminAlertPhoneNumber,
        elevenLabsAgentId: updates.elevenLabsAgentId,
      });
      if (workspaceId) {
        const current = agentsByWorkspace[workspaceId] || [];
        setAgentsForWorkspace(
          workspaceId,
          current.map((a) => (a.id === id ? updated : a)),
        );
      }
      return updated;
    },
    [agentsByWorkspace, setAgentsForWorkspace, workspaceId],
  );

  const updateAvatar = useCallback(
    async (id: string, avatarUrl: string) => {
      const updated = await apiUpdateAgentAvatar(id, {
        avatarType: 'UPLOADED',
        avatarUrl,
      });
      if (workspaceId) {
        const current = agentsByWorkspace[workspaceId] || [];
        setAgentsForWorkspace(
          workspaceId,
          current.map((a) => (a.id === id ? updated : a)),
        );
      }
      return updated;
    },
    [agentsByWorkspace, setAgentsForWorkspace, workspaceId],
  );

  const deleteAgent = useCallback(
    async (id: string) => {
      await apiDeleteAgent(id);
      if (workspaceId) {
        const current = agentsByWorkspace[workspaceId] || [];
        setAgentsForWorkspace(
          workspaceId,
          current.filter((a) => a.id !== id),
        );
      }
    },
    [agentsByWorkspace, setAgentsForWorkspace, workspaceId],
  );

  return {
    agents: (workspaceId && agentsByWorkspace[workspaceId]) || [],
    loading,
    error,
    reload,
    createAgent,
    saveAgent,
    updateAvatar,
    deleteAgent,
  };
};

